import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

const ApiContext = createContext();

// Cache configuration
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const REQUEST_DEDUP_WINDOW = 1000; // 1 second - deduplicate requests within this window

export const ApiProvider = ({ children }) => {
  const [cache, setCache] = useState({});
  const [loadingStates, setLoadingStates] = useState({});
  const pendingRequests = useRef({}); // Track pending requests to deduplicate

  // Get cached data if valid
  const getCachedData = useCallback((key) => {
    const cached = cache[key];
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }
    return null;
  }, [cache]);

  // Set cached data
  const setCachedData = useCallback((key, data) => {
    setCache(prev => ({
      ...prev,
      [key]: {
        data,
        timestamp: Date.now()
      }
    }));
  }, []);

  // Fetch with caching and deduplication
  const fetchData = useCallback(async (url, options = {}) => {
    const cacheKey = url;
    
    // Check cache first
    const cached = getCachedData(cacheKey);
    if (cached && !options.forceRefresh) {
      return cached;
    }

    // Check if request is already pending (deduplication)
    if (pendingRequests.current[cacheKey]) {
      // Wait for existing request to complete
      return pendingRequests.current[cacheKey];
    }

    // Set loading state
    setLoadingStates(prev => ({ ...prev, [cacheKey]: true }));

    // Create new request promise
    const requestPromise = fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      }
    })
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        const data = await res.json();
        
        // Cache the response
        setCachedData(cacheKey, data);
        
        return data;
      })
      .catch((err) => {
        console.error(`❌ Error fetching ${url}:`, err);
        throw err;
      })
      .finally(() => {
        // Remove from pending requests
        delete pendingRequests.current[cacheKey];
        setLoadingStates(prev => {
          const newState = { ...prev };
          delete newState[cacheKey];
          return newState;
        });
      });

    // Store pending request
    pendingRequests.current[cacheKey] = requestPromise;

    return requestPromise;
  }, [getCachedData, setCachedData]);

  // Convenience methods for common endpoints
  const api = {
    // Settings
    getSettings: useCallback((forceRefresh = false) => {
      return fetchData('https://mission.esoft.family/api/settings', { forceRefresh });
    }, [fetchData]),

    // Menus
    getMenus: useCallback((forceRefresh = false) => {
      return fetchData('https://mission.esoft.family/api/menus/all', { forceRefresh });
    }, [fetchData]),

    // News
    getNews: useCallback((limit = 10, page = 1, forceRefresh = false) => {
      const url = `https://mission.esoft.family/api/news?page=${page}&limit=${limit}`;
      return fetchData(url, { forceRefresh });
    }, [fetchData]),

    // Recent News
    getRecentNews: useCallback((limit = 2, forceRefresh = false) => {
      const url = `https://mission.esoft.family/api/news?limit=${limit}`;
      return fetchData(url, { forceRefresh });
    }, [fetchData]),

    // Publications
    getRecentPublications: useCallback((limit = 2, forceRefresh = false) => {
      const url = `https://mission.esoft.family/api/publications/recent?limit=${limit}`;
      return fetchData(url, { forceRefresh });
    }, [fetchData]),

    // Sliders
    getSliders: useCallback((forceRefresh = false) => {
      return fetchData('https://mission.esoft.family/api/sliders?active=true', { forceRefresh });
    }, [fetchData]),

    // Officials
    getOfficials: useCallback((forceRefresh = false) => {
      return fetchData('https://mission.esoft.family/api/officials', { forceRefresh });
    }, [fetchData]),

    // Introduction
    getIntroduction: useCallback((forceRefresh = false) => {
      return fetchData('https://mission.esoft.family/api/introduction', { forceRefresh });
    }, [fetchData]),

    // Gallery
    getRecentGallery: useCallback((limit = 6, forceRefresh = false) => {
      const url = `https://mission.esoft.family/api/gallery/recent?limit=${limit}`;
      return fetchData(url, { forceRefresh });
    }, [fetchData]),

    // Fapad News
    getRecentFapadNews: useCallback((limit = 4, forceRefresh = false) => {
      const url = `https://mission.esoft.family/api/fapad-news/recent?limit=${limit}`;
      return fetchData(url, { forceRefresh });
    }, [fetchData]),

    // Annual Activity Reports
    getRecentAnnualReports: useCallback((limit = 4, forceRefresh = false) => {
      const url = `https://mission.esoft.family/api/annual-activity-reports/recent?limit=${limit}`;
      return fetchData(url, { forceRefresh });
    }, [fetchData]),

    // Useful Links
    getUsefulLinks: useCallback((forceRefresh = false) => {
      return fetchData('https://mission.esoft.family/api/useful-links', { forceRefresh });
    }, [fetchData]),

    // Hotlines
    getHotlines: useCallback((forceRefresh = false) => {
      return fetchData('https://mission.esoft.family/api/hotlines', { forceRefresh });
    }, [fetchData]),

    // Media Corner
    getRecentMediaCorner: useCallback((limit = 2, forceRefresh = false) => {
      const url = `https://mission.esoft.family/api/media-corner/recent?limit=${limit}`;
      return fetchData(url, { forceRefresh });
    }, [fetchData]),

    // Generic fetch (for custom endpoints)
    fetch: fetchData,

    // Check loading state
    isLoading: useCallback((url) => {
      return loadingStates[url] || false;
    }, [loadingStates]),

    // Clear cache
    clearCache: useCallback(() => {
      setCache({});
      pendingRequests.current = {};
    }, []),

    // Clear specific cache entry
    clearCacheEntry: useCallback((url) => {
      setCache(prev => {
        const newCache = { ...prev };
        delete newCache[url];
        return newCache;
      });
    }, [])
  };

  return (
    <ApiContext.Provider value={api}>
      {children}
    </ApiContext.Provider>
  );
};

export const useApi = () => {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error('useApi must be used within ApiProvider');
  }
  return context;
};

